// FCM Token Utility - Simple Implementation
// This utility handles FCM token generation without complex notification handling
import { getToken } from 'firebase/messaging';
import { messaging } from '../config/firebase';

/**
 * Alternative FCM token generation that works without service worker
 * @returns {Promise<string|null>} FCM token or null if not available
 */
const getFCMTokenAlternative = async () => {
  try {
    // Check if we can use the legacy Firebase approach
    if (window.firebase && window.firebase.messaging) {
      console.log('Attempting legacy Firebase messaging approach...');

      const legacyMessaging = window.firebase.messaging();

      // Request permission
      const permission = await legacyMessaging.requestPermission();
      console.log('Legacy permission result:', permission);

      if (permission === 'granted') {
        // Get token using legacy approach
        const token = await legacyMessaging.getToken({
          vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
        });

        if (token) {
          console.log('FCM Token generated using legacy approach:', token);
          return token;
        }
      }
    }

    return null;
  } catch (error) {
    console.log('Legacy FCM approach failed:', error.message);
    return null;
  }
};

/**
 * Register service worker for FCM (optional - only if available)
 * @returns {Promise<ServiceWorkerRegistration|null>} Service worker registration or null
 */
const registerServiceWorker = async () => {
  try {
    if ('serviceWorker' in navigator) {
      // Only try to register if the service worker file exists
      try {
        const registration = await navigator.serviceWorker.register(
          '/firebase-messaging-sw.js',
          { type: 'module' },
        );
        console.log('Service Worker registered successfully:', registration);

        // Wait for the service worker to be ready
        await navigator.serviceWorker.ready;

        // Send Firebase configuration to the service worker
        if (registration.active) {
          const firebaseConfig = {
            apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
            authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
            projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
            storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
            messagingSenderId:
              process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
            appId: process.env.REACT_APP_FIREBASE_APP_ID,
          };

          try {
            registration.active.postMessage({
              type: 'FIREBASE_CONFIG',
              config: firebaseConfig,
            });
            console.log('Firebase configuration sent to service worker');
          } catch (msgError) {
            console.log('Could not send config to service worker');
          }
        }

        return registration;
      } catch (swError) {
        console.log(
          'Service Worker file not available, FCM will work without background notifications:',
          swError.message,
        );
        return null;
      }
    } else {
      console.log('Service Worker not supported');
      return null;
    }
  } catch (error) {
    console.log('Service Worker registration failed:', error.message);
    return null;
  }
};

/**
 * Get FCM token for device registration
 * @returns {Promise<string|null>} FCM token or null if not available
 */
export const getFCMToken = async () => {
  try {
    // Check if messaging is available
    if (!messaging) {
      console.log('Firebase messaging not available - FCM token not available');
      return null;
    }

    // Check notification permission
    if (Notification.permission !== 'granted') {
      console.log(
        'Notification permission not granted - FCM token not available',
      );
      return null;
    }

    // Debug: Check VAPID key
    const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
    if (!vapidKey) {
      console.error(
        'REACT_APP_FIREBASE_VAPID_KEY is not set - FCM token generation will fail',
      );
      return null;
    }
    console.log('VAPID Key available:', vapidKey ? 'Yes' : 'No');

    // Try to get token without service worker first (this should work for basic FCM)
    try {
      console.log('Attempting to get FCM token without service worker...');
      const token = await getToken(messaging, {
        vapidKey: vapidKey,
      });

      if (token) {
        console.log('FCM Token generated successfully:', token);
        return token;
      } else {
        console.log('FCM token generation returned null');
      }
    } catch (noSwError) {
      console.log('FCM token generation failed:', noSwError.message);
      console.log('Error details:', noSwError);

      // If basic token generation fails, try with service worker (if available)
      console.log('Attempting service worker registration as fallback...');
      const serviceWorkerRegistration = await registerServiceWorker();
      if (serviceWorkerRegistration) {
        try {
          console.log('Attempting to get FCM token with service worker...');
          const token = await getToken(messaging, {
            vapidKey: vapidKey,
            serviceWorkerRegistration: serviceWorkerRegistration,
          });

          if (token) {
            console.log('FCM Token generated with service worker:', token);
            return token;
          }
        } catch (swError) {
          console.log(
            'FCM token generation with service worker also failed:',
            swError.message,
          );
          console.log('Service worker error details:', swError);
        }
      } else {
        console.log(
          'Service worker registration failed, cannot use as fallback',
        );
      }
    }

    // Final fallback: try alternative approach
    console.log('Trying alternative FCM approach...');
    const alternativeToken = await getFCMTokenAlternative();
    if (alternativeToken) {
      return alternativeToken;
    }

    console.log(
      'No FCM token available - check Firebase configuration and VAPID key',
    );
    return null;
  } catch (error) {
    console.error('Error getting FCM token:', error);
    return null;
  }
};

/**
 * Request notification permission
 * @returns {Promise<string>} Permission status
 */
export const requestNotificationPermission = async () => {
  try {
    if (!('Notification' in window)) {
      console.log('This browser does not support notifications');
      return 'unsupported';
    }

    const permission = await Notification.requestPermission();
    console.log('Notification permission:', permission);
    return permission;
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return 'denied';
  }
};

/**
 * Check if FCM is supported
 * @returns {boolean} True if FCM is supported
 */
export const isFCMSupported = () => {
  return (
    'Notification' in window &&
    'serviceWorker' in navigator &&
    messaging !== null
  );
};
